// Permissions checking utilities
import type { Role, Group, Event, GroupMember } from '../types';
import { getGroupMembers, getGroup } from './firestore/groups';
import { getRSVP, getEvent } from './firestore/events';
import { isEventCohost } from './firestore/eventCohosts';

/**
 * Check if user can create subgroup in a group
 */
export const canCreateSubgroup = async (
  groupId: string,
  userId: string
): Promise<boolean> => {
  const member = await getGroupMember(groupId, userId);
  if (!member) return false;

  // Owner and cohosts can always create subgroups
  return member.role === 'owner' || member.role === 'cohost';
};

/**
 * Check if user can create event in context
 */
export const canCreateEvent = async (
  hostType: 'profile' | 'group',
  hostId: string,
  userId: string
): Promise<boolean> => {
  if (hostType === 'profile') {
    // Can always create events on their own profile
    return hostId === userId;
  }

  // For groups, check member role
  const member = await getGroupMember(hostId, userId);
  if (!member) return false;

  // Owner and cohosts can always create events
  // Members can if group rules allow
  if (member.role === 'owner' || member.role === 'cohost') {
    return true;
  }

  // Check group rules (would need to fetch group)
  const group = await getGroup(hostId);
  return group?.rules.membersCanInvite || false;
};

/**
 * Check if user can invite to group
 */
export const canInviteToGroup = async (
  groupId: string,
  userId: string
): Promise<boolean> => {
  const member = await getGroupMember(groupId, userId);
  if (!member) return false;

  if (member.role === 'owner' || member.role === 'cohost') {
    return true;
  }

  const group = await getGroup(groupId);
  return group?.rules.membersCanInvite || false;
};

/**
 * Check if user can invite to event
 */
export const canInviteToEvent = async (
  eventId: string,
  userId: string
): Promise<boolean> => {
  const event = await getEvent(eventId);
  if (!event) return false;

  // Event host (profile events) can always invite
  if (event.hostType === 'profile' && event.hostId === userId) {
    return true;
  }

  // Event creator or event cohost can always invite
  if (event.createdBy === userId) return true;
  if (await isEventCohost(eventId, userId)) return true;

  // If event is under a group: owner/cohost or members (if membersCanInvite)
  if (event.hostType === 'group') {
    const member = await getGroupMember(event.hostId, userId);
    if (!member) return false;
    if (member.role === 'owner' || member.role === 'cohost') return true;
    return !!event.rules?.membersCanInvite;
  }

  // Subevents (hostType === 'event'): creator and cohosts already allowed above; also allow parent event's group owner/cohost
  if (event.hostType === 'event') {
    const parentEvent = await getEvent(event.hostId);
    if (parentEvent?.hostType === 'group') {
      const member = await getGroupMember(parentEvent.hostId, userId);
      if (!member) return false;
      if (member.role === 'owner' || member.role === 'cohost') return true;
      return !!event.rules?.membersCanInvite;
    }
  }

  return false;
};

/**
 * Check if user can view attendees list
 */
export const canViewAttendees = async (
  event: Event,
  userId: string,
  viewerId: string
): Promise<boolean> => {
  // Event host/cohosts can always see
  if (event.hostId === viewerId) {
    return true;
  }

  if (event.hostType === 'group') {
    const member = await getGroupMember(event.hostId, viewerId);
    if (member && (member.role === 'owner' || member.role === 'cohost')) {
      return true;
    }
  }

  // Check visibility rules
  if (event.rules.attendeesVisible === 'off') {
    return false;
  }

  if (event.rules.attendeesVisible === 'mutuals_only') {
    // Would need to check mutual follows
    // For MVP, return false
    return false;
  }

  if (event.rules.attendeesVisible === 'friends_only') {
    // Would need to check if they're friends
    // For MVP, return false
    return false;
  }

  return false;
};

/**
 * Helper to get group member
 */
const getGroupMember = async (
  groupId: string,
  userId: string
): Promise<GroupMember | null> => {
  const members = await getGroupMembers(groupId);
  return members.find((m) => m.userId === userId) || null;
};


/**
 * Check if user can sponsor a member
 */
export const canSponsorMember = async (
  groupId: string,
  sponsorId: string
): Promise<{ can: boolean; reason?: string }> => {
  const group = await getGroup(groupId);
  if (!group) {
    return { can: false, reason: 'Group not found' };
  }

  const sponsor = await getGroupMember(groupId, sponsorId);
  if (!sponsor) {
    return { can: false, reason: 'Not a member of the group' };
  }

  // Only owner, cohost, or members can sponsor (not guests)
  if (sponsor.role !== 'owner' && sponsor.role !== 'cohost' && sponsor.role !== 'member') {
    return { can: false, reason: 'Insufficient role' };
  }

  // Check eligibility rules
  if (group.rules.sponsorEligibility?.minAttended) {
    // Would need to count attended events
    // For MVP, skip this check
  }

  // Check max sponsors limit
  if (group.rules.maxSponsorsPerMember) {
    // Would need to count how many people sponsorId has sponsored
    // For MVP, skip this check
  }

  return { can: true };
};




